import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { coffedRTUSettings } from '$lib/PersistedState.svelte';
import { modbusDebugEnabled } from '$lib/State.svelte';
import { invoke } from '@tauri-apps/api/core';
import { BaseDevice } from '../base';

class RTUClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RTUClientError';
  }
}

/**
 * Coffed SR30 roaster device using Modbus RTU
 *
 * Register addresses (from Artisan configuration):
 * - Register 0: Bean Temperature (BT) - uint16, divisor 2
 * - Register 10: Exhaust Temperature (ET) - uint16, divisor 2
 *
 * Serial connection settings:
 * - Baud Rate: 115200
 * - Data Bits: 8
 * - Stop Bits: 2
 * - Parity: None
 * - Timeout: 0.4 seconds
 *
 * Temperature conversion: temperature = raw_value / 2.0
 * Example: Raw value 325 → 325 / 2 = 162.5°C
 */
export class CoffedSR30Device extends BaseDevice {
  type: DeviceType = DeviceType.COFFED_SR30;
  private portName: string = '';
  private slaveId: number = 1;

  // Register addresses for Coffed SR30
  private static readonly REGISTERS = {
    // Bean Temperature (BT) - Register 0
    BEAN_TEMP: 0,
    // Exhaust Temperature (ET) - Register 10
    EXHAUST_TEMP: 10
  };

  // Coffed SR30 uses 115200 baud rate (unlike most Modbus devices which use 9600)
  private static readonly BAUD_RATE = 115200;

  protected async onConnect(): Promise<boolean> {
    this.portName = coffedRTUSettings.current.portName || '';
    this.slaveId = coffedRTUSettings.current.slaveId || 1;

    try {
      // Validate port name before attempting connection
      if (!this.portName || this.portName.trim() === '') {
        logger.error('Port name is not set. Cannot connect to Coffed SR30 RTU device.');
        return false;
      }

      logger.info(`Attempting to connect to Coffed SR30 RTU device on port: ${this.portName}`);

      // Define the registers we need to monitor for background reading
      const registersToMonitor = [
        CoffedSR30Device.REGISTERS.BEAN_TEMP, // 0 - Bean Temperature
        CoffedSR30Device.REGISTERS.EXHAUST_TEMP // 10 - Exhaust Temperature
      ];

      const connectParams = {
        portPath: this.portName,
        baudRate: CoffedSR30Device.BAUD_RATE, // 115200 baud for Coffed SR30
        slaveId: this.slaveId,
        registersToMonitor: registersToMonitor
      };

      logger.debug('Coffed SR30 RTU connect parameters:', connectParams);

      // Connect to RTU device and start background monitoring
      await invoke('connect_rtu', connectParams);

      logger.info(
        `Connected to Coffed SR30 RTU device on ${this.portName} with background monitoring for registers: ${registersToMonitor.join(', ')}`
      );
      return true;
    } catch (error) {
      logger.error(`Failed to connect to Coffed SR30 RTU device: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    invoke('disconnect_rtu', {
      portPath: this.portName,
      baudRate: CoffedSR30Device.BAUD_RATE,
      slaveId: this.slaveId
    }).catch((error) => logger.warn(`Coffed SR30 RTU disconnect error: ${error}`));
  }

  protected async updateReadings(): Promise<void> {
    try {
      // Read temperatures from both registers
      const beanTemp = await this.readTemperatureRegister(CoffedSR30Device.REGISTERS.BEAN_TEMP);
      const exhaustTemp = await this.readTemperatureRegister(
        CoffedSR30Device.REGISTERS.EXHAUST_TEMP
      );

      if (modbusDebugEnabled.current === true) {
        logger.debug('DEVICE_COFFED_SR30_READINGS', {
          beanTemp,
          exhaustTemp,
          rawBeanRegister: CoffedSR30Device.REGISTERS.BEAN_TEMP,
          rawExhaustRegister: CoffedSR30Device.REGISTERS.EXHAUST_TEMP
        });
      }

      // Coffed SR30 returns temperature values that need to be divided by 2
      // Temperature is already in °C
      const beanTempCelsius = beanTemp / 2.0;
      const exhaustTempCelsius = exhaustTemp / 2.0;

      this.localReadings = [
        beanTempCelsius, // TC1: Bean Temperature
        exhaustTempCelsius, // TC2: Exhaust Temperature
        null, // TC3: Not used
        null // TC4: Not used
      ];
    } catch (error) {
      if (error instanceof RTUClientError) {
        logger.error(`Coffed SR30 RTU error: ${error.message}`);
      } else {
        logger.error(`Coffed SR30 error: ${error}`);
      }
      throw error;
    }
  }

  /**
   * Read temperature from a register
   * Coffed SR30 uses single 16-bit registers (uint16) with divisor 2
   *
   * @param register Register address to read
   * @returns Raw temperature value (needs to be divided by 2)
   */
  private async readTemperatureRegister(register: number): Promise<number> {
    try {
      // Try to get cached value from background read loop
      const cachedValues = await this.getCachedRegisterValues([register]);

      if (cachedValues.has(register)) {
        const value = cachedValues.get(register)!;
        if (modbusDebugEnabled.current === true) {
          logger.debug(`Using cached temperature value from register ${register}: ${value}`);
        }
        return value;
      }

      // Fallback to direct read
      if (modbusDebugEnabled.current === true) {
        logger.debug(`No cached value available for register ${register}, performing direct read`);
      }

      const results: number[] = await invoke('read_rtu', {
        portPath: this.portName,
        baudRate: CoffedSR30Device.BAUD_RATE,
        slaveId: this.slaveId,
        reg: register,
        count: 1
      });

      if (results.length === 0) {
        throw new RTUClientError(`No data returned from register ${register}`);
      }

      return results[0];
    } catch (error) {
      logger.error(`Failed to read temperature register ${register}: ${error}`);
      throw error;
    }
  }

  /**
   * Get cached register values from the background read loop
   */
  private async getCachedRegisterValues(registers: number[]): Promise<Map<number, number>> {
    try {
      const result = (await invoke('get_rtu_latest_values', {
        portPath: this.portName,
        baudRate: CoffedSR30Device.BAUD_RATE,
        slaveId: this.slaveId,
        registers: registers
      })) as Record<number, number>;

      const cachedValues = new Map<number, number>();
      for (const [reg, value] of Object.entries(result)) {
        cachedValues.set(parseInt(reg), value);
      }

      return cachedValues;
    } catch (error) {
      logger.debug(`Failed to get cached register values: ${error}`);
      return new Map();
    }
  }
}

export default CoffedSR30Device;
